// Guardians' side of supervision on the circle page: the offer form ("Supervise"),
// the controls-and-guardians fields shared with supervised invites, and the
// guardian panel per supervised member (status, controls, sharing actions,
// pending requests with Approve/Decline, activity log).
import type { Schemas } from "@10twenty/api";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, ApiError, errorMessage, unwrap } from "../api";
import {
  availableControls,
  circleControls,
  changeOutcome,
  changeText,
  CONTROL_HINTS,
  CONTROL_LABELS,
  controlRows,
  controlsBody,
  decideErrorText,
  closedText,
  draftReady,
  eligibleGuardians,
  guards,
  hoursText,
  initialDraft,
  involves,
  logText,
  MODE_LABELS,
  MODES,
  namesText,
  PAUSE_CHOICES,
  pauseChoiceText,
  requestText,
  sharingView,
  statusLines,
  supervisionErrorText,
  type PauseChoice,
  type SupervisionDraft,
} from "../supervision/supervision";
import { useNow } from "../useNow";
import { useUnits } from "../useUnits";
import { BubbleForm } from "./BubbleForm";

type Circle = Schemas["Circle"];
type Member = Schemas["Member"];
type SupervisedMember = Schemas["SupervisedMember"];
type CircleRequest = Schemas["CircleSupervisionRequest"];

/** Picks up offers answered, requests and guardian actions from elsewhere this often. */
const SUPERVISIONS_POLL_MS = 30_000;
const TICK_MS = 30_000;
const LOG_PAGE = 20;

export const supervisionsKey = (circleId: string) => ["circle", circleId, "supervisions"];
export const logKey = (circleId: string) => ["circle", circleId, "supervision-log"];

/** After a supervision write: the panel, open activity logs and the member rows (badge, sharing). */
async function refreshSupervision(queryClient: QueryClient, circleId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: supervisionsKey(circleId) }),
    queryClient.invalidateQueries({ queryKey: logKey(circleId) }),
    queryClient.invalidateQueries({ queryKey: ["circle", circleId, "members"] }),
  ]);
}

/**
 * Refreshes the member rows once someone's supervision starts or ends here
 * (accepted, released or ended elsewhere), so badges and "Make admin" follow.
 */
export function useMembersFollowSupervisions(circleId: string, supervisions: SupervisedMember[] | undefined) {
  const queryClient = useQueryClient();
  const supervised = supervisions
    ?.filter((entry) => entry.supervision !== null)
    .map((entry) => entry.user_id)
    .sort()
    .join();
  const seen = useRef(supervised);
  useEffect(() => {
    if (supervised === undefined || supervised === seen.current) return;
    const first = seen.current === undefined;
    seen.current = supervised;
    if (!first) void queryClient.invalidateQueries({ queryKey: ["circle", circleId, "members"] });
  }, [supervised, circleId, queryClient]);
}

/** The supervisions the caller guards, is offered to guard, or is under here (admins and supervised members). */
export function useSupervisions(
  circleId: string,
  role: Circle["role"] | undefined,
  members: readonly Member[] | undefined,
  selfId: string,
) {
  const self = members?.find((m) => m.user_id === selfId);
  return useQuery({
    queryKey: supervisionsKey(circleId),
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}/supervisions", { params: { path: { circleId } } })),
    // Only admins can guard, and a supervised member sees their own.
    enabled: role === "admin" || self?.supervised === true,
    refetchInterval: SUPERVISIONS_POLL_MS,
  });
}

function writeError(error: unknown, name: string): string {
  if (error instanceof ApiError) {
    const text = supervisionErrorText(error.code, name);
    if (text) return text;
  }
  return errorMessage(error, {
    403: `Only ${name}'s guardians can do that.`,
    404: `${name} is no longer part of this circle, or nothing was pending.`,
  });
}

/** Controls and guardians, as an offer or a supervised invite sets them. */
export function SupervisionFields(props: {
  available: readonly string[];
  /** Admins who may guard (see `eligibleGuardians`). */
  candidates: readonly Pick<Member, "user_id" | "name">[];
  selfId: string;
  draft: SupervisionDraft;
  onChange: (draft: SupervisionDraft) => void;
}) {
  const { draft } = props;
  function toggleGuardian(userId: string, on: boolean) {
    const ids = draft.guardianIds.filter((id) => id !== userId);
    props.onChange({ ...draft, guardianIds: on ? [...ids, userId] : ids });
  }
  return (
    <div className="stack supervision-fields">
      <fieldset className="supervision-controls-field">
        <legend>Controls</legend>
        {availableControls(props.available).map((control) => (
          <label key={control}>
            <span>
              {CONTROL_LABELS[control]}
              <span className="hint"> {CONTROL_HINTS[control]}</span>
            </span>
            <select
              value={draft.controls[control] ?? "allow"}
              onChange={(event) =>
                props.onChange({
                  ...draft,
                  controls: { ...draft.controls, [control]: event.target.value as Schemas["SupervisionMode"] },
                })
              }
            >
              {MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {MODE_LABELS[mode]}
                </option>
              ))}
            </select>
          </label>
        ))}
      </fieldset>
      <fieldset className="supervision-guardians-field">
        <legend>Guardians</legend>
        <p className="hint">Admins who see the controls, act for them here and decide their requests.</p>
        {props.candidates.map((admin) => (
          <label key={admin.user_id} className="checkbox">
            <input
              type="checkbox"
              checked={draft.guardianIds.includes(admin.user_id)}
              onChange={(event) => toggleGuardian(admin.user_id, event.target.checked)}
            />
            {admin.name}
            {admin.user_id === props.selfId && " (you)"}
          </label>
        ))}
        {draft.guardianIds.length === 0 && <p className="error">Choose at least one guardian.</p>}
      </fieldset>
    </div>
  );
}

/** Offers supervision, or changes a running one (stricter parts become an offer). */
function SuperviseForm(props: {
  circle: Circle;
  members: readonly Member[];
  memberId: string;
  name: string;
  selfId: string;
  entry: SupervisedMember | null;
  onDone: () => void;
}) {
  const { circle, entry } = props;
  const queryClient = useQueryClient();
  const available = circleControls(circle);
  const [draft, setDraft] = useState(() => initialDraft(available, entry?.supervision, entry?.offer, props.selfId));
  const candidates = eligibleGuardians(props.members, props.memberId);
  const save = useMutation({
    mutationFn: async (vars: { circleId: string; userId: string; body: Schemas["SupervisionInput"] }) =>
      unwrap(
        await api.PUT("/circles/{circleId}/members/{userId}/supervision", {
          params: { path: { circleId: vars.circleId, userId: vars.userId } },
          body: vars.body,
        }),
      ),
    onSettled: async (_data, _error, vars) => {
      await refreshSupervision(queryClient, vars.circleId);
    },
  });
  const controls = controlsBody(draft.controls, available);
  const outcome = changeOutcome(entry?.supervision, controls, draft.guardianIds, Boolean(entry?.offer));
  const explanation = changeText(outcome, props.name, !entry?.supervision);

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate(
      { circleId: circle.id, userId: props.memberId, body: { controls, guardian_ids: draft.guardianIds } },
      { onSuccess: props.onDone },
    );
  }

  return (
    <form className="stack supervise-form" onSubmit={submit}>
      <h3>{entry?.supervision ? `Change ${props.name}'s supervision` : `Supervise ${props.name}`}</h3>
      {!entry?.supervision && (
        <p className="muted">
          Guardians decide whether {props.name} can pause sharing, share only an approximate area, leave this circle,
          delete their account or join other circles alone, with notice, or only by asking. {props.name} always sees the
          controls and every guardian action, and can stop supervision on their own after a notice period.
        </p>
      )}
      <SupervisionFields
        available={available}
        candidates={candidates}
        selfId={props.selfId}
        draft={draft}
        onChange={setDraft}
      />
      {explanation && <p className="hint">{explanation}</p>}
      {save.error && (
        <p className="error" role="alert">
          {writeError(save.error, props.name)}
        </p>
      )}
      <div className="button-row">
        <button type="submit" disabled={save.isPending || outcome.kind === "none" || !draftReady(draft, candidates)}>
          {entry?.supervision ? "Save" : entry?.offer ? "Update offer" : "Send offer"}
        </button>
        <button type="button" className="secondary" onClick={props.onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * The circle page's "Supervision" card: an offer form for `superviseFor`
 * (from a member row's "Supervise"), then a panel per member the caller
 * guards or is offered to guard.
 */
export function SupervisionCard(props: {
  circle: Circle;
  members: readonly Member[];
  selfId: string;
  supervisions: SupervisedMember[];
  superviseFor: string | null;
  onCloseSupervise: () => void;
}) {
  const { circle, selfId } = props;
  const entries = props.supervisions.filter((e) => e.user_id !== selfId && involves(e, selfId));
  const newMember =
    props.superviseFor && !entries.some((e) => e.user_id === props.superviseFor)
      ? props.members.find((m) => m.user_id === props.superviseFor)
      : undefined;
  const ref = useRef<HTMLElement>(null);
  const opened = newMember?.user_id;
  useEffect(() => {
    if (opened) ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [opened]);
  if (entries.length === 0 && !newMember) return null;
  return (
    <section className="card stack" id="supervision" ref={ref}>
      <h2>Supervision</h2>
      {newMember && (
        <SuperviseForm
          circle={circle}
          members={props.members}
          memberId={newMember.user_id}
          name={newMember.name}
          selfId={selfId}
          entry={null}
          onDone={props.onCloseSupervise}
        />
      )}
      {entries.map((entry) => (
        <SupervisedMemberPanel
          key={entry.user_id}
          circle={circle}
          members={props.members}
          selfId={selfId}
          entry={entry}
        />
      ))}
    </section>
  );
}

function SupervisedMemberPanel(props: {
  circle: Circle;
  members: readonly Member[];
  selfId: string;
  entry: SupervisedMember;
}) {
  const { circle, entry, selfId } = props;
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const guardian = guards(entry, selfId);
  const end = useMutation({
    mutationFn: async (vars: { circleId: string; userId: string }) => {
      const result = await api.DELETE("/circles/{circleId}/members/{userId}/supervision", {
        params: { path: vars },
      });
      // Already ended or withdrawn elsewhere is what we wanted.
      if (!result.response.ok && result.response.status !== 404) unwrap(result);
    },
    onSettled: async (_data, _error, vars) => {
      await refreshSupervision(queryClient, vars.circleId);
    },
  });

  function confirmEnd() {
    const question = entry.supervision
      ? `End ${entry.name}'s supervision? Their controls stop at once and they're told. Their sharing stays as it is.`
      : `Withdraw the supervision offer to ${entry.name}?`;
    if (!window.confirm(question)) return;
    // The log is only for current guardians: close it before the refresh would refetch it (403).
    setLogOpen(false);
    end.mutate({ circleId: circle.id, userId: entry.user_id });
  }

  const rows = controlRows(circleControls(circle), entry.supervision, entry.offer);
  // Without a running supervision any admin may revise or withdraw the offer.
  const canEdit = guardian || !entry.supervision;
  return (
    <article className="stack supervision-member" aria-label={`Supervision of ${entry.name}`}>
      <h3>
        {entry.name}
        <span className="supervised-badge">{entry.supervision ? "Supervised" : "Offered"}</span>
      </h3>
      {statusLines(entry).map((line) => (
        <p key={line} className="muted">
          {line}
        </p>
      ))}
      {entry.supervision && <p>Guardians: {namesText(entry.supervision.guardians, selfId)}</p>}
      {entry.offer && (!entry.supervision || offerChangesGuardians(entry)) && (
        <p>Guardians once accepted: {namesText(entry.offer.guardians, selfId)}</p>
      )}
      {editing ? (
        <SuperviseForm
          circle={circle}
          members={props.members}
          memberId={entry.user_id}
          name={entry.name}
          selfId={selfId}
          entry={entry}
          onDone={() => setEditing(false)}
        />
      ) : (
        <dl className="supervision-control-list">
          {rows.map((row) => (
            <div key={row.control}>
              <dt>{CONTROL_LABELS[row.control]}</dt>
              <dd>
                {row.mode && MODE_LABELS[row.mode]}
                {row.offered && (
                  <span className="muted">
                    {row.mode ? " → " : ""}
                    {MODE_LABELS[row.offered]}
                    {row.mode ? " once accepted" : ""}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {guardian && entry.supervision && <GuardianSharing circle={circle} entry={entry} selfId={selfId} />}
      {guardian && <PendingRequests circle={circle} entry={entry} />}
      {end.error && (
        <p className="error" role="alert">
          {writeError(end.error, entry.name)}
        </p>
      )}
      {!editing && canEdit && (
        <div className="button-row">
          <button type="button" className="secondary" onClick={() => setEditing(true)}>
            {entry.supervision ? "Change controls or guardians" : "Change offer"}
          </button>
          <button type="button" className="secondary danger" disabled={end.isPending} onClick={confirmEnd}>
            {entry.supervision ? "End supervision" : "Withdraw offer"}
          </button>
        </div>
      )}
      {guardian && (
        <div>
          <button type="button" className="link-button" aria-expanded={logOpen} onClick={() => setLogOpen(!logOpen)}>
            {logOpen ? "Hide activity" : "Activity"}
          </button>
          {logOpen && <SupervisionLog circle={circle} members={props.members} entry={entry} selfId={selfId} />}
        </div>
      )}
    </article>
  );
}

function offerChangesGuardians(entry: SupervisedMember): boolean {
  const now = entry.supervision?.guardians.map((g) => g.user_id).sort() ?? [];
  const offered = entry.offer?.guardians.map((g) => g.user_id).sort() ?? [];
  return now.join() !== offered.join();
}

type SharingChange =
  | { kind: "pause"; hours: PauseChoice }
  | { kind: "resume" }
  | { kind: "bubble"; radiusM: number; hours: number | null }
  | { kind: "endBubble" };

/** The member's sharing here, explained, with the guardian's pause/resume and bubble actions. */
function GuardianSharing({ circle, entry, selfId }: { circle: Circle; entry: SupervisedMember; selfId: string }) {
  const queryClient = useQueryClient();
  const units = useUnits();
  const nowMs = useNow(TICK_MS);
  const [hours, setHours] = useState<PauseChoice>(1);
  const [bubbleOpen, setBubbleOpen] = useState(false);
  const view = sharingView(entry, entry.sharing, selfId, nowMs, units);
  const change = useMutation({
    mutationFn: async (vars: { circleId: string; userId: string; change: SharingChange }) => {
      const path = { circleId: vars.circleId, userId: vars.userId };
      const c = vars.change;
      if (c.kind === "pause" || c.kind === "resume") {
        unwrap(
          await api.PATCH("/circles/{circleId}/members/{userId}", {
            params: { path },
            body: c.kind === "pause" ? { sharing: "paused", pause_hours: c.hours } : { sharing: "active" },
          }),
        );
      } else if (c.kind === "bubble") {
        unwrap(
          await api.PUT("/circles/{circleId}/members/{userId}/bubble", {
            params: { path },
            body: { radius_m: c.radiusM, duration_hours: c.hours },
          }),
        );
      } else {
        const result = await api.DELETE("/circles/{circleId}/members/{userId}/bubble", { params: { path } });
        if (!result.response.ok && result.response.status !== 404) unwrap(result);
      }
    },
    onSettled: async (_data, _error, vars) => {
      await refreshSupervision(queryClient, vars.circleId);
    },
  });

  function run(c: SharingChange, question?: string) {
    if (question && !window.confirm(question)) return;
    change.mutate({ circleId: circle.id, userId: entry.user_id, change: c });
  }

  const name = entry.name;
  const pausedByMember = entry.sharing.paused_by?.user_id === entry.user_id;
  return (
    <div className="stack supervision-sharing">
      <h4>Sharing</h4>
      {view.lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
      {change.error && (
        <p className="error" role="alert">
          {writeError(change.error, name)}
        </p>
      )}
      <div className="button-row">
        {view.canPause && (
          <>
            <label>
              Pause
              <select
                value={hours ?? ""}
                onChange={(event) =>
                  setHours(event.target.value === "" ? null : (Number(event.target.value) as PauseChoice))
                }
              >
                {PAUSE_CHOICES.map((choice) => (
                  <option key={choice ?? "open"} value={choice ?? ""}>
                    {pauseChoiceText(choice)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="secondary"
              disabled={change.isPending}
              onClick={() =>
                run(
                  { kind: "pause", hours },
                  `Pause ${name}'s sharing with ${circle.name} ${hours === null ? "until someone resumes it" : `for ${hoursText(hours)}`}? ${name} is told and can resume it.`,
                )
              }
            >
              Pause sharing
            </button>
          </>
        )}
        {view.canResume && (
          <button
            type="button"
            className="secondary"
            disabled={change.isPending}
            onClick={() =>
              run(
                { kind: "resume" },
                pausedByMember
                  ? `${name} paused sharing with ${circle.name} themselves. Resume it anyway? ${name} is told.`
                  : undefined,
              )
            }
          >
            Resume sharing
          </button>
        )}
        {view.canEndBubble && (
          <button
            type="button"
            className="secondary"
            disabled={change.isPending}
            onClick={() => run({ kind: "endBubble" })}
          >
            Share exact location again
          </button>
        )}
        {view.canStartBubble && !bubbleOpen && (
          <button type="button" className="secondary" disabled={change.isPending} onClick={() => setBubbleOpen(true)}>
            Share approximate area
          </button>
        )}
      </div>
      {bubbleOpen && view.canStartBubble && (
        <div className="stack">
          <p className="muted">
            {circle.name} will see an area around {name} instead of where they are. {name} is told and can end it.
          </p>
          <BubbleForm
            circleId={circle.id}
            offLabel="Until turned off"
            busy={change.isPending}
            onStart={(start) =>
              change.mutate(
                {
                  circleId: circle.id,
                  userId: entry.user_id,
                  change: { kind: "bubble", radiusM: start.radiusM, hours: start.hours },
                },
                { onSuccess: () => setBubbleOpen(false) },
              )
            }
            onCancel={() => setBubbleOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

/** The member's pending requests for this circle, with Approve/Decline and what became of decided ones. */
function PendingRequests({ circle, entry }: { circle: Circle; entry: SupervisedMember }) {
  const queryClient = useQueryClient();
  const units = useUnits();
  const [notes, setNotes] = useState<Record<string, string>>({});
  // Requests decided here this visit, to explain what became of them once they leave the pending list.
  const [decided, setDecided] = useState<Record<string, CircleRequest>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const decide = useMutation({
    mutationFn: async (vars: { circleId: string; request: CircleRequest; approve: boolean; note: string }) => {
      const path = { circleId: vars.circleId, requestId: vars.request.id };
      const body = { note: vars.note.trim() || null };
      return unwrap(
        vars.approve
          ? await api.POST("/circles/{circleId}/supervision-requests/{requestId}/approve", { params: { path }, body })
          : await api.POST("/circles/{circleId}/supervision-requests/{requestId}/decline", { params: { path }, body }),
      );
    },
    onSuccess: (result, vars) => {
      setDecided((prev) => ({ ...prev, [vars.request.id]: result }));
      setErrors(({ [vars.request.id]: _, ...rest }) => rest);
    },
    onError: (error, vars) => {
      const text =
        (error instanceof ApiError && decideErrorText(error.code, entry.name)) ||
        errorMessage(error, { 403: `Only ${entry.name}'s guardians can decide this.` });
      setErrors((prev) => ({ ...prev, [vars.request.id]: text }));
    },
    onSettled: async (_data, _error, vars) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: supervisionsKey(vars.circleId) }),
        queryClient.invalidateQueries({ queryKey: ["circle", vars.circleId] }),
      ]);
    },
  });

  function run(request: CircleRequest, approve: boolean) {
    const question = approve
      ? `Approve this request? ${requestText(request, entry.name, units)}. It's carried out as soon as every guardian it needs has approved.`
      : `Decline? ${entry.name} is told.`;
    if (!window.confirm(question)) return;
    decide.mutate({ circleId: circle.id, request, approve, note: notes[request.id] ?? "" });
  }

  const pending = entry.requests.filter((r) => r.status === "pending");
  const pendingIds = new Set(pending.map((r) => r.id));
  // A pending request this circle approved stays listed (it waits for another circle) with its decision note.
  const closed = Object.values(decided).filter((r) => !pendingIds.has(r.id));
  const orphanErrors = Object.entries(errors).filter(([id]) => !pendingIds.has(id));
  if (pending.length === 0 && closed.length === 0 && orphanErrors.length === 0) return null;
  return (
    <div className="stack supervision-requests">
      <h4>Requests</h4>
      <ul className="stack">
        {pending.map((request) => (
          <li key={request.id} className="supervision-request stack">
            <p>
              <strong>{requestText(request, entry.name, units)}</strong>
            </p>
            {request.note && <p className="supervision-note">“{request.note}”</p>}
            <p className="muted">
              Asked {new Date(request.created_at).toLocaleString()} · expires{" "}
              {new Date(request.expires_at).toLocaleString()}
            </p>
            {request.needs_approval_elsewhere && (
              <p className="hint">It also needs a guardian's approval in another circle.</p>
            )}
            {request.approval && (
              <p className="muted">
                Approved here by {request.approval.approved_by?.name ?? "a guardian"}; waiting for another circle.
              </p>
            )}
            <label>
              Reply (optional)
              <input
                maxLength={140}
                value={notes[request.id] ?? ""}
                onChange={(event) => setNotes((prev) => ({ ...prev, [request.id]: event.target.value }))}
              />
            </label>
            <div className="button-row">
              {/* Once approved here it only waits for another circle; any guardian may still decline. */}
              {!request.approval && (
                <button type="button" disabled={decide.isPending} onClick={() => run(request, true)}>
                  Approve
                </button>
              )}
              <button
                type="button"
                className="secondary danger"
                disabled={decide.isPending}
                onClick={() => run(request, false)}
              >
                Decline
              </button>
            </div>
            {errors[request.id] && (
              <p className="error" role="alert">
                {errors[request.id]}
              </p>
            )}
          </li>
        ))}
        {closed.map((request) => (
          <li key={request.id} className="supervision-request stack">
            <p>{requestText(request, entry.name, units)}</p>
            <p className={request.status === "failed" || request.status === "stale" ? "error" : "muted"} role="status">
              {closedText(request, entry.name)}
            </p>
          </li>
        ))}
      </ul>
      {orphanErrors.map(([id, text]) => (
        <p key={id} className="error" role="alert">
          {text}
        </p>
      ))}
    </div>
  );
}

/** The member's supervision log, newest first, a page at a time (also the member's own, on their card). */
export function SupervisionLog(props: {
  circle: Pick<Circle, "id">;
  /** Resolves names in entries (guardians dropped and the like). */
  members: readonly Pick<Member, "user_id" | "name">[];
  entry: Pick<SupervisedMember, "user_id" | "name">;
  selfId: string;
}) {
  const { circle, entry } = props;
  const units = useUnits();
  const log = useInfiniteQuery({
    queryKey: [...logKey(circle.id), entry.user_id],
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET("/circles/{circleId}/supervision-log", {
          params: {
            path: { circleId: circle.id },
            query: { member: entry.user_id, limit: LOG_PAGE, ...(pageParam ? { page_token: pageParam } : {}) },
          },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_page_token,
    refetchInterval: SUPERVISIONS_POLL_MS,
  });
  const nameOf = (userId: string) => props.members.find((m) => m.user_id === userId)?.name;
  const lines = (log.data?.pages ?? [])
    .flatMap((page) => page.items)
    .map((item) => ({ item, text: logText(item, entry.name, props.selfId, nameOf, undefined, units) }))
    .filter((line): line is { item: (typeof line)["item"]; text: string } => line.text !== null);
  return (
    <div className="stack supervision-log">
      {log.isPending && <p className="muted">Loading activity…</p>}
      {log.error && <p className="error">{errorMessage(log.error)}</p>}
      {log.data && lines.length === 0 && <p className="muted">Nothing yet.</p>}
      {lines.length > 0 && (
        <ul>
          {lines.map(({ item, text }) => (
            <li key={item.id}>
              <span className="muted">{new Date(item.at).toLocaleString()}</span> · {text}
            </li>
          ))}
        </ul>
      )}
      {log.hasNextPage && (
        <button
          type="button"
          className="link-button"
          disabled={log.isFetchingNextPage}
          onClick={() => void log.fetchNextPage()}
        >
          Older activity
        </button>
      )}
    </div>
  );
}
